import { isDefined } from '@e-socna/utils';
import type {
  RetryStrategyFn,
  RetryContext,
  RetryDecision,
} from '@e-socna/better-fetch';

import {
  handleNetworkError,
  calculateMinDelayForRate,
  handleServerError,
} from '../../utils/strategy.util';
import { MAX_RETRY_DELAY_MS } from '../../proxy/constants/delay.constant';

/**
 * Retry strategy for georisques API
 * No official rate limits found, using conservative approach similar to geo
 *
 * @example
 * ```typescript
 * import { georisquesRetryStrategy } from '@e-socna/gis';
 *
 * const data = await betterFetch(url, {}, {
 *   retryStrategy: georisquesRetryStrategy,
 * });
 * ```
 */

export const georisquesRetryStrategy: RetryStrategyFn = ({
  statusCode,
  attempt,
  retryAfterMs,
}: RetryContext): RetryDecision => {
  // Network errors and timeouts
  if (typeof statusCode === 'undefined') {
    return handleNetworkError(attempt);
  }

  // 429 Rate Limit: respect Retry-After or use conservative 10 req/s
  if (statusCode === 429) {
    if (isDefined(retryAfterMs)) {
      return {
        shouldRetry: true,
        delayMs: Math.min(retryAfterMs, MAX_RETRY_DELAY_MS),
      };
    }

    // Conservative: assume 10 req/s limit
    const minDelay = calculateMinDelayForRate(10);
    const backoffDelay = minDelay * 2 ** attempt;

    return {
      shouldRetry: true,
      delayMs: Math.min(backoffDelay, MAX_RETRY_DELAY_MS),
    };
  }

  // 5xx Server errors
  if (statusCode >= 500 && statusCode < 600) {
    return handleServerError(attempt);
  }

  return { shouldRetry: false, delayMs: 0 };
};
